// TA-10892 #33: members the Owner invited during Project Creation onboarding,
// flagged `assigned` once they are assigned during Task Creation onboarding.
// After taskCreationComplete the web chat list auto-opens the chat of the
// single invited-and-assigned member, or highlights 2+ such members' chats.

import { DeviceEventEmitter } from 'react-native';

export const ONBOARDING_INVITED_MEMBERS_KEY = 'onboarding_invited_members';
/** Emitted after invitees are recorded so a mounted chat list re-checks. */
export const ONBOARDING_INVITEES_UPDATED = 'ONBOARDING_INVITEES_UPDATED';

export type OnboardingInvitee = {
  email?: string;
  id?: string;
  assigned?: boolean;
};

type InviteeChat = {
  id: string;
  name?: string | null;
  isGroupChat?: boolean | null;
  userIds?: (string | null)[] | null;
  pendingInvitations?: ({ email?: string | null } | null)[] | null;
};

const normalize = (invitee: OnboardingInvitee): OnboardingInvitee | null => {
  const email = invitee.email?.trim().toLowerCase() || undefined;
  const id = invitee.id || undefined;
  if (!email && !id) return null;
  return invitee.assigned ? { email, id, assigned: true } : { email, id };
};

export const readOnboardingInvitees = (): OnboardingInvitee[] => {
  try {
    const parsed = JSON.parse(
      globalThis.localStorage?.getItem?.(ONBOARDING_INVITED_MEMBERS_KEY) ?? '[]'
    ) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((i) => (i && typeof i === 'object' ? normalize(i) : null))
      .filter((i): i is OnboardingInvitee => !!i);
  } catch {
    return [];
  }
};

/** Adds invitees, merging entries that share an email or id. */
export const addOnboardingInvitees = (invitees: OnboardingInvitee[]): void => {
  mergeInvitees(invitees);
};

/**
 * Flags people assigned during Task Creation onboarding. Anyone assigned but
 * not yet recorded (e.g. an email first invited from the task) is added as
 * invited-and-assigned.
 */
export const markOnboardingInviteesAssigned = (
  assignees: OnboardingInvitee[]
): void => {
  mergeInvitees(assignees.map((a) => ({ ...a, assigned: true })));
};

export const getAssignedOnboardingInvitees = (): OnboardingInvitee[] =>
  readOnboardingInvitees().filter((i) => i.assigned);

const mergeInvitees = (invitees: OnboardingInvitee[]): void => {
  const merged = readOnboardingInvitees();
  invitees.forEach((raw) => {
    const invitee = normalize(raw);
    if (!invitee) return;
    const existing = merged.find(
      (m) =>
        (!!invitee.email && m.email === invitee.email) ||
        (!!invitee.id && m.id === invitee.id)
    );
    if (existing) {
      existing.email ??= invitee.email;
      existing.id ??= invitee.id;
      if (invitee.assigned) existing.assigned = true;
    } else {
      merged.push(invitee);
    }
  });
  if (merged.length === 0) return;
  try {
    globalThis.localStorage?.setItem?.(
      ONBOARDING_INVITED_MEMBERS_KEY,
      JSON.stringify(merged)
    );
  } catch {
    // Storage unavailable (private mode) — the chat list just won't react.
    return;
  }
  DeviceEventEmitter.emit(ONBOARDING_INVITEES_UPDATED);
};

export const clearOnboardingInvitees = (): void => {
  try {
    globalThis.localStorage?.removeItem?.(ONBOARDING_INVITED_MEMBERS_KEY);
  } catch {
    // ignore
  }
};

/**
 * 1:1 chats belonging to the invitees, one per invitee (first match wins).
 * A chat matches by the invitee's user id in `userIds`, or by email against a
 * pending invitation / the pending chat's name (email-only invitees).
 */
export const findInviteeChats = <T extends InviteeChat>(
  chats: T[],
  invitees: OnboardingInvitee[],
  meId: string | undefined
): T[] => {
  const result: T[] = [];
  invitees.forEach((invitee) => {
    const match = chats.find((chat) => {
      if (chat.isGroupChat || result.includes(chat)) return false;
      if (
        invitee.id &&
        invitee.id !== meId &&
        chat.userIds?.includes(invitee.id)
      )
        return true;
      if (!invitee.email) return false;
      return (
        chat.name?.toLowerCase() === invitee.email ||
        !!chat.pendingInvitations?.some(
          (p) => p?.email?.toLowerCase() === invitee.email
        )
      );
    });
    if (match) result.push(match);
  });
  return result;
};
